// ============================================================================
// src/app/navigation/AppLink.tsx — the facade's declarative navigation link.
// ============================================================================
// WHY THIS EXISTS
// Exactly two declarative links exist in the tree today
// (src/dashboard/ProductionPage.jsx, src/shared/CommentsSection.tsx), both
// internal. This wrapper keeps them behind the same seam as imperative
// navigation:
//
//   * `to` must be an internal absolute path ('/'-leading, never
//     protocol-relative, no whitespace/control characters/backslashes) —
//     anything else is a programmer error and throws, in every environment;
//   * `state` and `replace` obey the same contract as appNavigate: state is
//     normalized through the hostile-safe snapshot (an unsafe payload throws
//     a bounded TypeError instead of reaching the router raw), and replace
//     must be a strict boolean when provided;
//   * every other prop (className, style, data-*, onClick, children, target,
//     download, reloadDocument) passes through to the react-router Link
//     untouched, and the ref forwards to the real anchor;
//   * this component never reads an event property itself: modifier-key,
//     middle-click, preventDefault, and reloadDocument behavior are the
//     underlying Link's, inherited verbatim;
//   * external URLs do not belong here: the tree already renders those as
//     plain external anchors, and that stays as it is.
// ============================================================================
import React from 'react';
import {resolveNavigationDriver} from './navigationDriver.js';
import {normalizeNavigationState} from './navigationState.js';
import {assertInternalAbsolutePath} from './appNavigate.js';

/**
 * `to` is the only prop this component owns outright. `state` is `unknown`
 * because normalizeNavigationState is what decides whether a payload is safe —
 * declaring a shape here would let a caller past the check the facade exists to
 * perform — and the index signature carries every pass-through prop
 * (className, style, data-*, onClick, children, target, download,
 * reloadDocument) to the underlying Link untouched.
 */
export type AppLinkProps = {
  to: string;
  state?: unknown;
  replace?: boolean;
  [prop: string]: unknown;
};

export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(props, ref) {
  // `props || {}` is kept: every consumer is still JavaScript, and React itself
  // guarantees an object only for elements it constructs. The annotation states
  // that each field may be absent so the guard below still has something to
  // guard — assertInternalAbsolutePath takes `unknown` and throws for anything
  // that is not an internal absolute path, which is the real gate on `to`.
  const {to, state, replace, ...rest}: Partial<AppLinkProps> = props || {};
  assertInternalAbsolutePath(to, 'AppLink `to`');
  const forwarded: {state?: unknown; replace?: boolean} = {};
  if (state !== undefined) {
    const normalized = normalizeNavigationState(state);
    if (!normalized.ok) {
      throw new TypeError(`AppLink state is not a safe navigation payload (${normalized.reason})`);
    }
    forwarded.state = normalized.value;
  }
  if (replace !== undefined) {
    if (typeof replace !== 'boolean') {
      throw new TypeError('AppLink `replace` must be a strict boolean when provided');
    }
    forwarded.replace = replace;
  }
  const LinkComponent = resolveNavigationDriver().LinkComponent;
  return React.createElement(LinkComponent, {to, ...rest, ...forwarded, ref});
});
